import promptManifest from "../content/word-command-prompts.json";

export type AuthoredCheckPrompt = {
  id: string;
  assessmentForm: "context_cloze" | "sense_discrimination";
  stem: string;
  options: string[];
  answer: string;
};

export type AuthoredWordPrompt = {
  contextId: string;
  readingId: string;
  word: string;
  senseId: string;
  checks: AuthoredCheckPrompt[];
  use: {
    id: string;
    assessmentForm: "self_attested_production";
    direction: string;
    criteria: Array<{ id: string; label: string }>;
  };
  natural: {
    id: string;
    assessmentForm: "self_reported_transfer";
    cue: string;
  };
};

export type WordCommandManifest = {
  schemaVersion: "vocabulary-review-v3";
  policyVersion: "word-command-v2";
  reviewCorpus: "contextual-senses-v2";
  packs: AuthoredWordPrompt[];
};

export type LegacyWordCommandManifest = {
  schemaVersion: "vocabulary-review-v2";
  policyVersion: "word-command-v2";
  reviewCorpus: "contextual-words-v1";
  packs: Record<string, Omit<AuthoredWordPrompt, "contextId" | "readingId">>;
};

export type WordCommandPromptLookup = {
  word: string;
  readingId?: string;
  senseId?: string;
};

export type WordCommandPromptIndexes = {
  byContext: ReadonlyMap<string, AuthoredWordPrompt>;
  byWord: ReadonlyMap<string, readonly AuthoredWordPrompt[]>;
};

export function contextualPromptIdentity(contextId: string, localPromptId: string) {
  return `${contextId}#${localPromptId}`;
}

export function buildWordCommandPromptIndexes(
  manifest: WordCommandManifest,
): WordCommandPromptIndexes {
  const byContext = new Map<string, AuthoredWordPrompt>();
  const byWord = new Map<string, AuthoredWordPrompt[]>();
  for (const pack of manifest.packs) {
    if (byContext.has(pack.contextId)) {
      throw new Error(`Duplicate contextual prompt identity: ${pack.contextId}`);
    }
    byContext.set(pack.contextId, pack);
    byWord.set(pack.word, [...(byWord.get(pack.word) ?? []), pack]);
  }
  return { byContext, byWord };
}

export function findWordCommandPrompt(
  lookup: WordCommandPromptLookup,
  indexes: WordCommandPromptIndexes = WORD_COMMAND_PROMPT_INDEXES,
) {
  if (lookup.senseId) {
    const pack = indexes.byContext.get(lookup.senseId);
    if (!pack || pack.word !== lookup.word) return undefined;
    if (lookup.readingId && pack.readingId !== lookup.readingId) return undefined;
    return pack;
  }
  const candidates = indexes.byWord.get(lookup.word) ?? [];
  const matches = lookup.readingId
    ? candidates.filter((pack) => pack.readingId === lookup.readingId)
    : candidates;
  return matches.length === 1 ? matches[0] : undefined;
}

export function hasWordCommandPrompt(
  word: string,
  indexes: WordCommandPromptIndexes = WORD_COMMAND_PROMPT_INDEXES,
) {
  return (indexes.byWord.get(word)?.length ?? 0) === 1;
}

export function toLegacyWordCommandManifest(
  manifest: WordCommandManifest,
): LegacyWordCommandManifest {
  const packs: LegacyWordCommandManifest["packs"] = {};
  for (const pack of manifest.packs) {
    if (packs[pack.word]) {
      throw new Error(
        `Cannot project contextual prompt packs for “${pack.word}” into the legacy word-keyed backend manifest.`,
      );
    }
    packs[pack.word] = {
      word: pack.word,
      senseId: pack.senseId,
      checks: pack.checks,
      use: pack.use,
      natural: pack.natural,
    };
  }
  return {
    schemaVersion: "vocabulary-review-v2",
    policyVersion: manifest.policyVersion,
    reviewCorpus: "contextual-words-v1",
    packs,
  };
}

export const WORD_COMMAND_MANIFEST = promptManifest as WordCommandManifest;
export const WORD_COMMAND_PROMPT_INDEXES = buildWordCommandPromptIndexes(WORD_COMMAND_MANIFEST);
